<template>
  <v-data-iterator
    :items="sortedClients"
    :items-per-page="20"
    :loading="loading"
    style="width: 100%;"
    return-object
  >
    <template v-slot:loader>
      <v-row class="api-clients-list-loading-spinner">
        <v-col class="text-center">
          <v-progress-circular
            indeterminate
            size="70"
            width="7"
            color="primary"
            :aria-label="$t('routes.apiClients.loadingList')"
          />
          <div>{{ $t('routes.apiClients.loadingList') }}</div>
        </v-col>
      </v-row>
    </template>

    <template v-slot:no-data>
      <v-row class="api-clients-list-no-results">
        <v-col class="text-center">
          <div>{{ $t('routes.apiClients.noClientsFound') }}</div>
        </v-col>
      </v-row>
    </template>

    <template v-slot:header>
      <div class="d-flex api-client-list-view-row api-clients-list-view-header-row">
        <div @click="setSortBy('name')" data-cy="console-apiClients-sortByName">
          {{ $t('routes.apiClients.name') }}
          <span v-if="sortBy === 'name'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div @click="setSortBy('clientId')" data-cy="console-apiClients-sortByClientId">
          {{ $t('routes.apiClients.clientId') }}
          <span v-if="sortBy === 'clientId'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div @click="setSortBy('user')" data-cy="console-apiClients-sortByUser">
          {{ $t('routes.apiClients.user') }}
          <span v-if="sortBy === 'user'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div @click="setSortBy('status')" data-cy="console-apiClients-sortByStatus">
          {{ $t('routes.apiClients.status') }}
          <span v-if="sortBy === 'status'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div @click="setSortBy('createdAt')" data-cy="console-apiClients-sortByDate">
          {{ $t('routes.apiClients.createdAt') }}
          <span v-if="sortBy === 'createdAt'">
            <v-icon size="small" v-show="sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="!sortDesc">mdi-sort-descending</v-icon>
          </span>
        </div>
        <div>
          <!-- Actions column header -->
        </div>
      </div>
    </template>

    <template v-slot:default="{ items }">
      <div class="mt-2">
        <div v-for="item in items" :key="item.value.id">
          <ApiClientItem
            :item="item.value"
            class="list-view-item api-client-list-view-row align-center"
            @deleteClient="$emit('deleteClient', $event)"
            @toggleStatus="$emit('toggleStatus', $event.clientId, $event.active)"
            @editClient="$emit('editClient', $event)"
          />
        </div>
      </div>
    </template>

    <template v-slot:footer="{ pagination, options }">
      <v-data-table-footer
        :pagination="pagination"
        :options="options"
        :items-per-page-options="footerOptions['items-per-page-options']"
        style="font-size: 10px;"
      />
    </template>
  </v-data-iterator>
</template>

<script>
import ApiClientItem from './ApiClientItem.vue'

export default {
  name: "ApiClientsListView",
  components: {
    ApiClientItem,
  },
  props: {
    clients: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['deleteClient', 'toggleStatus', 'editClient'],
  data() {
    return {
      sortBy: 'createdAt',
      sortDesc: true,
    };
  },
  methods: {
    setSortBy(sortByColumn) {
      if (this.sortBy === sortByColumn) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortDesc = false;
        this.sortBy = sortByColumn;
      }
    },
    sortByName(a, b) {
      return (a.name || '').localeCompare(b.name || '');
    },
    sortByClientId(a, b) {
      return (a.client_id || '').localeCompare(b.client_id || '');
    },
    sortByUser(a, b) {
      const userA = a.user_name || a.user_email || '';
      const userB = b.user_name || b.user_email || '';
      return userA.localeCompare(userB);
    },
    sortByStatus(a, b) {
      return (a.active === b.active) ? 0 : a.active ? -1 : 1;
    },
    sortByCreatedAt(a, b) {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateA - dateB;
    },
  },
  computed: {
    sortedClients() {
      let items = [...this.clients];

      switch (this.sortBy) {
        case 'name': items.sort(this.sortByName); break;
        case 'clientId': items.sort(this.sortByClientId); break;
        case 'user': items.sort(this.sortByUser); break;
        case 'status': items.sort(this.sortByStatus); break;
        case 'createdAt': items.sort(this.sortByCreatedAt); break;
        default: break;
      }

      if (this.sortDesc) {
        return items.reverse();
      }

      return items;
    },
    footerOptions() {
      return {
        'items-per-page-options': [10, 20, 30, 50],
      };
    },
  },
}
</script>

<style>
.api-clients-list-view-header-row {
  font-weight: 600;
  cursor: pointer;
  height: 54px;
  color: #000000;
  background-color: #3dabff;
  border-radius: 10px;
  align-items: center;
}

.api-clients-list-view-header-row i.v-icon {
  color: #000000;
}

.api-client-list-view-row {
  padding-left: 8px;
  padding-right: 8px;
}

.api-client-list-view-row > div {
  display: flex;
  align-items: center;
  word-break: normal;
}

.api-client-list-view-row > div:first-child {
  text-align: left;
}

.api-client-list-view-row > div:last-child {
  text-align: right;
}

/* Normal screens */
.api-client-list-view-row > div:nth-child(1) { width: 20%; }    /* name */
.api-client-list-view-row > div:nth-child(2) { width: 15%; }    /* client_id */
.api-client-list-view-row > div:nth-child(3) { width: 25%; }    /* user */
.api-client-list-view-row > div:nth-child(4) { width: 10%; }    /* status */
.api-client-list-view-row > div:nth-child(5) { width: 20%; }    /* created_at */
.api-client-list-view-row > div:nth-child(6) { width: 10%; justify-content: end; }    /* actions */

/* Small screens */
@media only screen and (max-width: 960px) {
  .api-client-list-view-row > div:nth-child(1) { width: 30%; }    /* name */
  .api-client-list-view-row > div:nth-child(2) { display: none; }    /* client_id */
  .api-client-list-view-row > div:nth-child(3) { display: none; }    /* user */
  .api-client-list-view-row > div:nth-child(4) { width: 20%; }    /* status */
  .api-client-list-view-row > div:nth-child(5) { width: 35%; }    /* created_at */
  .api-client-list-view-row > div:nth-child(6) { width: 15%; }    /* actions */
}

.api-clients-list-loading-spinner {
  padding-top: 24px;
}
</style>
